import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { AppCluster, AppKeyService, IAppKey } from '@app/services/appkey';
import { ICompany } from '@app/services/company';
import { DialogClass, DialogService } from '@app/services/dialog';
import { ProgressIndicatorService } from '@app/services/progress';
import { FeedbackService } from '@services/feedback';
import { StorageService, StorageType } from '@services/storage';
import { forkJoin } from 'rxjs';

import { ObjectString } from '../../../../../../../shared/utilities';
import { IAppRequiredTerms, LegalsService } from '../../../../../../services/legals';
import { AppKeyEditDialogComponent, EditAppKey } from '../appkey-edit-dialog';
import { AppKeyLegals, AppKeyLegalsDialogComponent } from '../appkey-legals-dialog';
import { AppKeyNewDialogComponent, NewAppKey } from '../appkey-new-dialog';
import { AppKeyRequiredVersionsDialogComponent,	AppKeyRequiredVersionsDialogData } from '../appkey-required-versions-dialog';
import { AppKeyTableActions, AppKeyTableStorageKeys } from './appkey-table.models';

@Component({
	selector: 'app-keys-table',
	templateUrl: './appkey-table.component.html',
	styleUrls: ['./appkey-table.component.scss']
})
export class AppKeyTableComponent implements OnInit, OnChanges {
	@Input() company: ICompany;
	@Input() editable: boolean;

	@ViewChild(MatMenuTrigger, { static: false }) menu: MatMenuTrigger;

	public appKeys: IAppKey[];
	public appKeysFiltered: IAppKey[];
	public appKeysPaginated: IAppKey[];
	public appKeyClusters: AppCluster[];
	public clusters: ObjectString<any> = {};
	public providers: string[] = [];
	public loading = false;
	public clusterControl = new FormControl('');
	public providerControl = new FormControl('');
	public appKeyControl = new FormControl('');
	public pageSize = 10;
	public pageIndex = 0;
	public openedMenu: number;

	constructor(
		private dialogService: DialogService,
		private appKeyService: AppKeyService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private storageService: StorageService,
		private legalsService: LegalsService
	) {}

	public ngOnInit(): void {
		this.clusterControl.valueChanges.subscribe((value: string) => {
			this.storageService.setItem(StorageType.sessionStorage, AppKeyTableStorageKeys.clusterFilter, value, this.company.id);
			this.filterAppKeyResults();
			if (this.menu) {
				this.menu.closeMenu();
			}

			this.clusterControl.setValue(value, { emitEvent: false });
		});

		this.providerControl.valueChanges.subscribe((value: string) => {
			this.storageService.setItem(StorageType.sessionStorage, AppKeyTableStorageKeys.providerFilter, value, this.company.id);
			this.filterAppKeyResults();
			if (this.menu) {
				this.menu.closeMenu();
			}

			this.providerControl.setValue(value, { emitEvent: false });
		});

		this.appKeyControl.valueChanges.subscribe((value: string) => {
			this.filterAppKeyResults();
			this.appKeyControl.setValue(value, { emitEvent: false });
		});
	}

	public ngOnChanges(): void {
		this.getAppKeys();
		this.getClusters();
	}

	public openAddAppKeyDialog(): void {
		this.dialogService.open(AppKeyNewDialogComponent, {
			data: {
				company: this.company,
				appkey: {
					name: '',
					companyId: this.company.id,
					accessTokenExpiration: 60,
					refreshTokenExpiration: 525600
				},
				callback: (appKey: IAppKey) => {
					if (appKey) this.getAppKeys(false, false);
				}
			} as NewAppKey
		});
	}

	public openEditAppKeyDialog(appKey: IAppKey): void {
		this.getAppKey(appKey, (result: IAppKey) => {
			this.dialogService.open(AppKeyEditDialogComponent, {
				data: {
					company: this.company,
					appkey: result,
					cluster: this.clusters[result.clusterId],
					callback: (action: AppKeyTableActions, updatedAppKey: IAppKey) => {
						this.appKeyDialogCallback(action, appKey);

						if (updatedAppKey) this.getAppKeys();
					}
				} as EditAppKey
			});
		});
	}

	public openAppKeyLegalsDialog(appKey: IAppKey): void {
		this.progressIndicatorService.spinner(true);

		this.legalsService.getClientIdTerms(appKey.clientId).subscribe(
			(terms: IAppRequiredTerms) => {
				this.progressIndicatorService.spinner(false);

				this.dialogService.open(AppKeyLegalsDialogComponent, {
					data: {
						editable: this.editable,
						company: this.company,
						appKey: appKey,
						terms: terms,
						cluster: this.clusters[appKey.clusterId],
						callback: (action: AppKeyTableActions) => {
							this.appKeyDialogCallback(action, appKey);
						}
					} as AppKeyLegals,
					panelClass: DialogClass.large
				});
			},
			(error: any) => {
				this.feedbackService.apiError(error);
				this.progressIndicatorService.spinner(false);
			}
		);
	}

	public openRequiredVersionsDialog(appKey: IAppKey): void {
		this.dialogService.open(AppKeyRequiredVersionsDialogComponent, {
			data: {
				appKey
			} as AppKeyRequiredVersionsDialogData,
			panelClass: DialogClass.large
		});
	}

	public paginateResults(event: PageEvent, resetPageIndex = false): void {
		this.pageIndex = resetPageIndex || event?.pageSize !== this.pageSize ? 0 : event ? event.pageIndex : this.pageIndex;
		this.pageSize = event ? event.pageSize : this.pageSize;

		this.appKeysPaginated = this.appKeysFiltered
			.sort((a: IAppKey, b: IAppKey) => a.name.localeCompare(b.name))
			.slice(this.pageIndex * this.pageSize, this.pageIndex * this.pageSize + this.pageSize);
	}

	private appKeyDialogCallback(action: AppKeyTableActions, appKey: IAppKey): void {
		switch (action) {
			case AppKeyTableActions.edit:
				this.openEditAppKeyDialog(appKey);
				break;
			case AppKeyTableActions.legals:
				this.openAppKeyLegalsDialog(appKey);
				break;
			case AppKeyTableActions.requiredVersions:
				this.openRequiredVersionsDialog(appKey);
				break;
		}
	}

	private filterAppKeyResults(): void {
		if (this.appKeys) {
			this.appKeysFiltered = this.appKeys
				.filter((appKey) => {
					return this.providerControl.value ? appKey.provider === this.providerControl.value : true;
				})
				.filter((appKey) => {
					return this.clusterControl.value ? appKey.clusterId === this.clusterControl.value : true;
				})
				.filter((appKey) => {
					return this.appKeyControl.value && this.appKeyControl.value.trim()
						? appKey.clientId.toLowerCase().includes(this.appKeyControl.value.toLowerCase().trim()) ||
								appKey.name.toLowerCase().includes(this.appKeyControl.value.toLowerCase().trim())
						: true;
				});

			this.paginateResults(null, true);
		}
	}

	private getAppKey(appkey: IAppKey, callback?: (data: IAppKey) => void) {
		this.progressIndicatorService.spinner(true);

		this.appKeyService.get(appkey.clientId).subscribe(
			(result: IAppKey) => {
				this.loading = false;
				this.appKeys = this.appKeys.map((a) => (a.clientId === result.clientId ? result : a));

				this.progressIndicatorService.spinner(false);

				if (callback) {
					callback(result);
				}
			},
			() => {
				this.loading = false;
				this.progressIndicatorService.spinner(false);
			}
		);
	}

	private getAppKeys(force = false, loader = true) {
		this.loading = true;

		if (force) {
			this.appKeyService.clearCache();
		}

		this.appKeyService.getAll(force).subscribe(
			(appKeys: ObjectString<any>) => {
				this.appKeys = Object.values(appKeys);

				const providers = [];

				this.appKeys.forEach((appKey) => {
					if (appKey.provider) {
						providers.push(appKey.provider);
					}
				});

				if (this.appKeys.length < 2) {
					this.appKeyControl.setValue('');
				}

				this.providers = Array.from(new Set(providers)).sort((a, b) => a.localeCompare(b));

				this.isLoaded();
				setTimeout(() => this.filterAppKeyResults());
				if (loader) this.progressIndicatorService.spinner(false);
			},
			(error: any) => {
				this.loading = false;
				this.appKeys = [];
				if (loader) this.progressIndicatorService.spinner(false);
				this.feedbackService.apiError(error);
			}
		);
	}

	private getClusters(force = false) {
		this.loading = true;

		this.appKeyService.getClusters(force).subscribe((result: AppCluster[]) => {
			result.forEach((c) => {
				this.clusters[c.clusterId] = c;
			});
			this.isLoaded();
		});
	}

	private getAndSetFilters() {
		const clusterFilter = this.storageService.getItem(
			StorageType.sessionStorage,
			AppKeyTableStorageKeys.clusterFilter,
			this.company.id
		);
		const providerFilter = this.storageService.getItem(
			StorageType.sessionStorage,
			AppKeyTableStorageKeys.providerFilter,
			this.company.id
		);

		if (this.appKeyClusters.findIndex((cluster) => cluster.clusterId === clusterFilter) > -1) {
			this.clusterControl.setValue(clusterFilter);
		} else {
			this.clusterControl.setValue('');
		}

		if (this.providers.findIndex((provider) => provider === providerFilter) > -1) {
			this.providerControl.setValue(providerFilter);
		} else {
			this.providerControl.setValue('');
		}
	}

	private getAppKeyClusters() {
		const clusters = [];

		this.appKeys.forEach((appKey) => {
			if (this.clusters[appKey.clusterId]) {
				clusters.push(this.clusters[appKey.clusterId]);
			}
		});

		this.appKeyClusters = Array.from(new Set(clusters)).sort((a, b) => a.name.localeCompare(b.name));
	}

	private isLoaded() {
		if (this.appKeys && Object.entries(this.clusters).length > 0) {
			this.getAppKeyClusters();
			this.getAndSetFilters();
			this.loading = false;
		}
	}
}
